import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, CalendarCheck, Inbox, Phone, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useNavigate } from "react-router-dom";

const TONE = { new: "warn", replied: "primary", closed: "good", confirmed: "primary", completed: "good", cancelled: "bad" };

export default function Enquiries() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("demos");

  const load = () =>
    api
      .get("/admin/enquiries")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.status === 403 ? "Admin access only" : "Could not load enquiries"));

  useEffect(() => { load(); }, []);

  const setStatus = async (kind, id, status) => {
    try {
      await api.put(`/admin/${kind}/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const s = data?.stats;

  return (
    <div className="min-h-screen" data-testid="enquiries-page">
      <header className="h-14 border-b border-border flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-20 bg-background/85 backdrop-blur-md">
        <Logo size="sm" />
        <button onClick={() => navigate("/projects")} className="ml-auto text-[13px] text-muted-foreground hover:text-foreground" data-testid="enquiries-back-btn">
          Back to portfolio
        </button>
      </header>

      <div className="p-4 sm:p-6 max-w-[1200px] mx-auto">
        <PageHead title="Enquiries & demos" subtitle="Everything submitted from the public website, with the same records that were emailed to you." />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <Kpi label="New enquiries" value={s?.new_contacts ?? "—"} tone={s?.new_contacts ? "warn" : "good"} testId="kpi-new-contacts" />
          <Kpi label="Upcoming demos" value={s?.upcoming_demos ?? "—"} tone="primary" testId="kpi-upcoming-demos" />
          <Kpi label="Total enquiries" value={s?.total_contacts ?? "—"} testId="kpi-total-contacts" />
          <Kpi label="Total demos booked" value={s?.total_demos ?? "—"} testId="kpi-total-demos" />
        </div>

        <div className="flex gap-2 mb-4">
          {[["demos", "Demo bookings", CalendarCheck], ["contacts", "Contact enquiries", Inbox]].map(([key, text, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 border px-3 py-1.5 text-[12.5px] transition-colors duration-150 ${
                tab === key ? "border-[#F97316] text-[#F97316] bg-[#F97316]/10" : "border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`enquiries-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {text}
            </button>
          ))}
        </div>

        {tab === "demos" ? (
          <Panel title="Demo bookings" testId="demos-panel">
            <div className="overflow-x-auto">
              <table className="w-full dense-table">
                <thead className="sticky-head">
                  <tr><th>Slot (IST)</th><th>Company</th><th>Contact</th><th>Team</th><th>Notes</th><th>Status</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {(data?.demos || []).map((d) => (
                    <tr key={d.id} data-testid={`demo-row-${d.id}`}>
                      <td className="num whitespace-nowrap">{fmtDate(d.date)} · {d.time}</td>
                      <td className="font-medium">{d.company}</td>
                      <td>
                        <div>{d.name}</div>
                        <div className="num text-[11px] text-muted-foreground">{d.email}</div>
                        <div className="num text-[11px] text-muted-foreground">{d.phone}</div>
                      </td>
                      <td className="whitespace-nowrap">{d.team_size}</td>
                      <td className="max-w-[280px] text-muted-foreground text-[11.5px]">{d.notes || "—"}</td>
                      <td><Badge2 tone={TONE[d.status]}>{d.status}</Badge2></td>
                      <td>
                        <div className="flex gap-1.5">
                          {d.status !== "completed" ? (
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus("demos", d.id, "completed")} data-testid={`demo-complete-${d.id}`}>Done</Button>
                          ) : null}
                          {d.status !== "cancelled" ? (
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus("demos", d.id, "cancelled")} data-testid={`demo-cancel-${d.id}`}>Cancel</Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data && !data.demos.length ? <tr><td colSpan={7} className="text-muted-foreground">No demo bookings yet.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </Panel>
        ) : (
          <Panel title="Contact enquiries" testId="contacts-panel">
            <div className="space-y-2.5">
              {(data?.contacts || []).map((c) => (
                <div key={c.id} className="border border-border bg-card p-4" data-testid={`contact-row-${c.id}`}>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <Building2 className="w-4 h-4 text-[#F97316]" />
                    <span className="font-medium text-[13.5px]">{c.company}</span>
                    <Badge2 tone={TONE[c.status]}>{c.status}</Badge2>
                    <span className="text-[11px] text-muted-foreground">{c.subject}</span>
                    <span className="num text-[11px] text-muted-foreground ml-auto">{String(c.created_at).slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2.5 text-[12px] text-muted-foreground">
                    <span>{c.name}</span>
                    <a href={`mailto:${c.email}`} className="num flex items-center gap-1.5 hover:text-[#F97316]"><Mail className="w-3.5 h-3.5" />{c.email}</a>
                    {c.phone ? <span className="num flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{c.phone}</span> : null}
                  </div>
                  <p className="text-[12.5px] mt-3 leading-relaxed whitespace-pre-wrap border-l-2 border-[#F97316] pl-3">{c.message}</p>
                  <div className="flex gap-1.5 mt-3.5">
                    <a href={`mailto:${c.email}?subject=Re: ${encodeURIComponent(c.subject)} — Plan-Sarthi`}>
                      <Button size="sm" className="h-7 text-[11px]" data-testid={`contact-reply-${c.id}`}>Reply by email</Button>
                    </a>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus("enquiries", c.id, "replied")} data-testid={`contact-replied-${c.id}`}>Mark replied</Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus("enquiries", c.id, "closed")} data-testid={`contact-closed-${c.id}`}>Close</Button>
                  </div>
                </div>
              ))}
              {data && !data.contacts.length ? <p className="text-[12.5px] text-muted-foreground">No enquiries yet.</p> : null}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}
